import { createReducer } from 'redux-starter-kit';
import { handleError } from '../../utils/handleError';
import i18next from 'i18next';
import Dict from '../../utils/Dict';
import { MonitoringApi } from '../../_helpers/service';

/**
 * Constants
 */
export const monitoringModule = 'monitoring';
const LOADING = `${monitoringModule}/LOADING`;
const SET_DATA = `${monitoringModule}/SET_DATA`;
const CLEAR_STATE = `${monitoringModule}/CLEAR_STATE`;
const TABLE_DATA = `${monitoringModule}/TABLE_DATA`;
const SERVICE_PROVIDER_LIST = `${monitoringModule}/SERVICE_PROVIDER_LIST`;
const LOADING_EXPORT_DATA = `${monitoringModule}/LOADING_EXPORT_DATA`;

/**
 * Reducer
 */
const initialState = {
  loading: false,
  loadingExport: false,
  data: [],
  tableData: {
    content: [],
    totalElements: 0
  },
  serviceProviderList: {
    content: [],
    totalElements: 0
  }
};

export default createReducer(initialState, {
  [LOADING]: (state, action) => {
    state.loading = action.payload;
  },
  [SET_DATA]: (state, action) => {
    state.data = action.payload;
  },
  [TABLE_DATA]: (state, action) => {
    const filter = action.filter || {};
    if (filter.pageNumber && filter.pageNumber !== 1) {
      state.tableData.content = [...state.tableData.content, ...action.payload.content];
    } else {
      state.tableData = action.payload;
    }
  },
  [SERVICE_PROVIDER_LIST]: (state, action) => {
    const filter = action.filter || {};
    if (filter.pageNumber && filter.pageNumber !== 1) {
      state.serviceProviderList.content = [
        ...state.serviceProviderList.content,
        ...action.payload.content
      ];
    } else {
      state.serviceProviderList = action.payload;
    }
  },
  [LOADING_EXPORT_DATA]: (state, action) => {
    state.loadingExport = action.payload;
  },
  [CLEAR_STATE]: () => initialState
});

/**
 * Actions
 */
export const clearState = () => ({ type: CLEAR_STATE });

export const loadData = filter => async dispatch => {
  try {
    const { fromDate, toDate } = filter;
    if (fromDate && toDate) {
      dispatch({ type: LOADING, payload: true });
      const dictServices = await Dict.itemsObject('gu_services');
      const dictKato = await Dict.itemsObject('60');
      const response = await MonitoringApi.loadData(fromDate, toDate);
      const data = response.data.content.map(item => {
        let result = {};
        for (const column of item.passportValues) {
          result[column.code] = column.value;
          if (column.code === 'service_type') {
            const value = column.value
              .replace('REQUEST_FORM_', '')
              .replace('request_form_', '');
            const serviceItem =
              dictServices[value.toUpperCase()] ||
              dictServices[value.toLowerCase()] ||
              {};
            result[column.code] = value;
            result.service = serviceItem;
            result.ru_serviceName = serviceItem.ru_name;
            result.kk_serviceName = serviceItem.kk_name;
          }
          if (column.code === 'area_code') {
            const locationItem = dictKato[column.value] || {};
            result.location = locationItem.code || 'none';
            result.ru_locationName = locationItem.ru_name;
            result.kk_locationName = locationItem.kk_name;
          }
        }
        return result;
      });
      dispatch({ type: SET_DATA, payload: data });
    }
  } catch (error) {
    handleError(error, i18next.t('monitoringDucks_errorLoadTable'));
  } finally {
    dispatch({ type: LOADING, payload: false });
  }
};

export const loadChildVisit = filter => async dispatch => {
  try {
    dispatch({ type: LOADING, payload: true });
    const { data } = await MonitoringApi.loadChildVisit(JSON.stringify(filter));
    const dictKato = await Dict.itemsObject('60');
    for (let row of data.result.content) {
      row.location = dictKato[row.legalLocality] || {};
    }
    dispatch({ type: TABLE_DATA, payload: data.result, filter });
  } catch (error) {
    handleError(error, i18next.t('monitoringDucks_errorLoadTable'));
  } finally {
    dispatch({ type: LOADING, payload: false });
  }
};
export const loadServiceProviderList = filter => async dispatch => {
  try {
    dispatch({ type: LOADING, payload: true });
    const queryString = new URLSearchParams(filter).toString();

    const response = await MonitoringApi.loadServiceProviderList(queryString);
    const dictTypeCode = await Dict.itemsObject('gu_type_code');
    const dictKato = await Dict.itemsObject('60');
    const dictSubType = await Dict.itemsObject('230');
    // const dictFormOwnership = await Dict.itemsObject('gu_form_ownership');

    const data = [];

    response.data.content.forEach(item => {
      const typeCodeEntry = item.passportValues.find(p => p.code === 'type_code');
      const originalTypeCode = typeCodeEntry?.value;

      if (originalTypeCode !== '01' && originalTypeCode !== '02') {
        return;
      }

      const result = {};

      for (const column of item.passportValues) {
        const { code, value } = column;

        if (code === 'type_code') {
          result.type_code = dictTypeCode[value] || {};
        } else if (code === 'form_ownership') {
          result.form_ownership = value;
        } else if (code === 'sub_types') {
          const subTypesArray = JSON.parse(value || '[]');
          result.sub_types = subTypesArray.map(el => dictSubType[el]);
        } else if (code === 'legal_locality') {
          result.legal_locality = value;
          result.district = dictKato[value] || {};
          result.location = (value && dictKato[value.slice(0, 4).padEnd(9, '0')]) || {};
        } else {
          result[code] = value;
        }
      }

      data.push(result);
    });

    dispatch({
      type: SERVICE_PROVIDER_LIST,
      payload: { content: [...data], totalElements: response.data.totalElements },
      filter
    });
  } catch (error) {
    handleError(error, i18next.t('monitoringDucks_errorLoadTable'));
  } finally {
    dispatch({ type: LOADING, payload: false });
  }
};

export const loadStaffVisit = filter => async dispatch => {
  try {
    dispatch({ type: LOADING, payload: true });
    const { data } = await MonitoringApi.loadStaffVisit(JSON.stringify(filter));
    const dictKato = await Dict.itemsObject('60');
    for (let row of data.result.content) {
      row.location = dictKato[row.legalLocality] || {};
    }
    dispatch({ type: TABLE_DATA, payload: data.result, filter });
  } catch (error) {
    handleError(error, i18next.t('monitoringDucks_errorLoadTable'));
  } finally {
    dispatch({ type: LOADING, payload: false });
  }
};

export const exportData = filter => async dispatch => {
  try {
    dispatch({ type: LOADING_EXPORT_DATA, payload: true });
    const response = await MonitoringApi.exportDataChild(JSON.stringify(filter));
    download(
      response.data,
      `${i18next.t('monitoring_children_preschool_education')}.xlsx`
    );
  } catch (error) {
    handleError(error, i18next.t('monitoring_exportError'));
  } finally {
    dispatch({ type: LOADING_EXPORT_DATA, payload: false });
  }
};

export const exportDataStaff = filter => async dispatch => {
  try {
    dispatch({ type: LOADING_EXPORT_DATA, payload: true });
    const response = await MonitoringApi.exportDataStaff(JSON.stringify(filter));
    download(response.data, `${i18next.t('monitoring_staff_visits_organizations')}.xlsx`);
  } catch (error) {
    handleError(error, i18next.t('monitoring_exportError'));
  } finally {
    dispatch({ type: LOADING_EXPORT_DATA, payload: false });
  }
};

export const exportDataServiceProviders = () => async dispatch => {
  try {
    dispatch({ type: LOADING_EXPORT_DATA, payload: true });
    const response = await MonitoringApi.exportDataServiceProviders();
    download(response.data, `${i18next.t('monitoring_staff_list_organizations')}.xlsx`);
  } catch (error) {
    handleError(error, i18next.t('monitoring_exportError'));
  } finally {
    dispatch({ type: LOADING_EXPORT_DATA, payload: false });
  }
};

const download = (data, fileName) => {
  let element = document.createElement('a');
  document.body.appendChild(element);
  element.style.display = 'none';
  let blob = new Blob([data]);
  let url = window.URL.createObjectURL(blob);
  element.href = url;
  element.download = fileName;
  element.click();
  window.URL.revokeObjectURL(url);
};
